import { z } from 'zod';

import { BoListQueryBase } from '@Common/types/bo/bo_list_query';
import { buildZodBoListQueryParsed } from '@Common/types/bo/utils/build_zod_bo_list_query';
import { BOExternalAdminRolesValues } from '@Common/types/bo_external/auth/admin_user';
import { ZodIDPathParam, ZodId } from '@Common/types/request';
import { ZodSuccessResponse } from '@Common/types/response';
import { createZodArrayable } from '@Common/types/zod/zod-arrayable';
import { ZodBrandedDateString } from '@Common/types/zod/zod-branded-date';
import { ZodCountryCode } from '@Common/types/zod/zod-country-code';
import { ZodSecureEmail } from '@Common/types/zod/zod-email';
import { endpoint, GetEndpointType } from '@Common/types/zod/zod-endpoint';
import { ZodMobilePhoneNumber, ZodPhoneNumber } from '@Common/types/zod/zod-phone-number';
import { ZodTrimmedString } from '@Common/types/zod/zod-trimmed-string';

export const ZodB2bAdminStatus = z.enum(['pending', 'active', 'inactive', 'invited']);
export type B2bAdminStatus = z.infer<typeof ZodB2bAdminStatus>;

export const B2bAdminRolesValues = [
  'admin_level_1',
  'admin_level_2',
  'admin_level_3',
  'admin_level_4',
] as const satisfies readonly (typeof BOExternalAdminRolesValues)[number][];
export const ZodB2bAdminRoles = z.enum(B2bAdminRolesValues);
export type B2bAdminRoles = z.TypeOf<typeof ZodB2bAdminRoles>;

export const B2bAdminsLanguagePreferenceValues = ['FR', 'EN'] as const;
export const ZodB2bAdminsLanguagePreference = z.enum(B2bAdminsLanguagePreferenceValues);
export type B2bAdminsLanguagePreference = z.infer<typeof ZodB2bAdminsLanguagePreference>;

export const B2bAdminGenderValues = ['male', 'female'] as const;
export const ZodB2bAdminGender = z.enum(B2bAdminGenderValues);
export type B2bAdminGender = z.infer<typeof ZodB2bAdminGender>;

export const B2bAdminBillingChannelValues = ['paper', 'email', 'both'] as const;
export const ZodB2bAdminBillingChannel = z.enum(B2bAdminBillingChannelValues);
export type B2bAdminBillingChannel = z.infer<typeof ZodB2bAdminBillingChannel>;

/**
 * Mailing / billing correspondence of an admin. An empty address means the company address is used.
 * Cross-field rules are checked by `validateAdminCorrespondence`.
 */
const ZodB2bAdminCorrespondenceBase = z.object({
  is_mailing_correspondent: z.boolean(),
  // Empty strings are rejected, the forms send null for an empty input
  mailing_street: ZodTrimmedString.min(1).nullable(),
  mailing_postalcode: ZodTrimmedString.min(1).nullable(),
  mailing_city: ZodTrimmedString.min(1).nullable(),
  mailing_country: ZodCountryCode.nullable(),
  is_npai_mailing: z.boolean(),
  npai_mailing_at: ZodBrandedDateString.nullable(),
  is_billing_correspondent: z.boolean(),
  billing_channel: ZodB2bAdminBillingChannel.nullable(),
  billing_street: ZodTrimmedString.min(1).nullable(),
  billing_postalcode: ZodTrimmedString.min(1).nullable(),
  billing_city: ZodTrimmedString.min(1).nullable(),
  billing_country: ZodCountryCode.nullable(),
  is_npai_billing: z.boolean(),
  npai_billing_at: ZodBrandedDateString.nullable(),
});

const b2bAdminCorrespondenceKeys = Object.fromEntries(
  Object.keys(ZodB2bAdminCorrespondenceBase.shape).map((key) => [key, true as const]),
) as { [K in keyof z.TypeOf<typeof ZodB2bAdminCorrespondenceBase>]: true };

// On creation a missing field means "not a correspondent"
const ZodB2bAdminCorrespondenceCreateRequest = z.object({
  is_mailing_correspondent:
    ZodB2bAdminCorrespondenceBase.shape.is_mailing_correspondent.default(false),
  mailing_street: ZodB2bAdminCorrespondenceBase.shape.mailing_street.default(null),
  mailing_postalcode: ZodB2bAdminCorrespondenceBase.shape.mailing_postalcode.default(null),
  mailing_city: ZodB2bAdminCorrespondenceBase.shape.mailing_city.default(null),
  mailing_country: ZodB2bAdminCorrespondenceBase.shape.mailing_country.default(null),
  is_npai_mailing: ZodB2bAdminCorrespondenceBase.shape.is_npai_mailing.default(false),
  npai_mailing_at: ZodB2bAdminCorrespondenceBase.shape.npai_mailing_at.default(null),
  is_billing_correspondent:
    ZodB2bAdminCorrespondenceBase.shape.is_billing_correspondent.default(false),
  billing_channel: ZodB2bAdminCorrespondenceBase.shape.billing_channel.default(null),
  billing_street: ZodB2bAdminCorrespondenceBase.shape.billing_street.default(null),
  billing_postalcode: ZodB2bAdminCorrespondenceBase.shape.billing_postalcode.default(null),
  billing_city: ZodB2bAdminCorrespondenceBase.shape.billing_city.default(null),
  billing_country: ZodB2bAdminCorrespondenceBase.shape.billing_country.default(null),
  is_npai_billing: ZodB2bAdminCorrespondenceBase.shape.is_npai_billing.default(false),
  npai_billing_at: ZodB2bAdminCorrespondenceBase.shape.npai_billing_at.default(null),
});

// On edition (also exposed by the public API) an omitted field keeps the stored value, like `gender`,
// and `null` clears it. The BO forms always send their full state.
const ZodB2bAdminCorrespondenceEditRequest = ZodB2bAdminCorrespondenceBase.partial();

export const ZodGetB2bAdminsOneResponse = z.object({
  id: ZodId,
  company_id: ZodId,
  email: ZodSecureEmail,
  email_state: z.enum(['blocked', 'permanently_blocked', 'invalid']).nullable(),
  status: ZodB2bAdminStatus,
  created_at: z.date(),
  updated_at: z.date(),
  firstname: ZodTrimmedString.nullable(),
  lastname: ZodTrimmedString.nullable(),
  gender: ZodB2bAdminGender.nullable(),
  phone_number: ZodPhoneNumber.nullable(),
  is_migrated: z.boolean(),
  role_in_company: ZodTrimmedString.nullable(),
  role: ZodB2bAdminRoles.nullable(),
  email_verified: z.boolean(),
  last_invitation_sent_at: z.date().nullable(),
  language_preference: ZodB2bAdminsLanguagePreference,
  is_third_party: z.boolean(),
  third_party_legal_structure: ZodTrimmedString.nullable(),
  third_party_mandate_document_id: ZodId.nullable(),
  secondary_email: ZodTrimmedString.nullable(),
  secondary_email_state: z.enum(['blocked', 'permanently_blocked', 'invalid']).nullable(),
  ...ZodB2bAdminCorrespondenceBase.shape,
});
export type GetB2bAdminsOneResponse = z.TypeOf<typeof ZodGetB2bAdminsOneResponse>;

export const ZodGetB2bAdminsShowResponse = ZodGetB2bAdminsOneResponse.extend({
  has_last_otp: z.boolean(),
});
export type GetB2bAdminsShowResponse = z.TypeOf<typeof ZodGetB2bAdminsShowResponse>;

export const ZodGetB2bAdminsResponse = z.array(
  ZodGetB2bAdminsOneResponse.omit({
    email_state: true,
    secondary_email_state: true,
    ...b2bAdminCorrespondenceKeys,
  }),
);
export type GetB2bAdminsResponse = z.TypeOf<typeof ZodGetB2bAdminsResponse>;

export const getB2bAdminsSortColumns: (keyof Omit<GetB2bAdminsResponse[number], 'company_id'>)[] = [
  'id',
  'email',
  'status',
  'created_at',
  'updated_at',
  'firstname',
  'lastname',
  'phone_number',
  'email_verified',
];

const ZodFilters = z
  .object({
    ...ZodGetB2bAdminsOneResponse.pick({
      id: true,
      company_id: true,
      email: true,
      status: true,
      firstname: true,
      lastname: true,
      phone_number: true,
      email_verified: true,
    }).shape,
    from_created_at: createZodArrayable(ZodTrimmedString),
    to_created_at: createZodArrayable(ZodTrimmedString),
  })
  .partial();

export const ZodGetB2bAdminsQuery = buildZodBoListQueryParsed(ZodFilters, getB2bAdminsSortColumns, {
  range: [0, 100],
});

export type GetB2bAdminsQuery = BoListQueryBase<
  z.TypeOf<typeof ZodFilters>,
  (typeof getB2bAdminsSortColumns)[number]
>;

export const ZodThirdPartyMandateDocumentUpload = z.object({
  presigned_path: ZodTrimmedString,
  document_name: ZodTrimmedString,
});
export type ThirdPartyMandateDocumentUpload = z.TypeOf<typeof ZodThirdPartyMandateDocumentUpload>;

// On edit, signals that the admin's current mandate document should be kept as-is
// (no re-upload). The actual document id is resolved server-side for security.
export const ZodThirdPartyMandateDocumentKeep = z.object({
  keep_existing: z.literal(true),
});
export const ZodEditThirdPartyMandateDocument = z.union([
  ZodThirdPartyMandateDocumentUpload,
  ZodThirdPartyMandateDocumentKeep,
]);
export type EditThirdPartyMandateDocument = z.TypeOf<typeof ZodEditThirdPartyMandateDocument>;

export const ZodPostCreateB2bAdminRequest = z.object({
  ...ZodGetB2bAdminsOneResponse.pick({
    email: true,
    company_id: true,
    is_third_party: true,
  }).shape,
  third_party_legal_structure: ZodTrimmedString.nullable().default(null),
  third_party_mandate_document: ZodThirdPartyMandateDocumentUpload.nullable().default(null),
  is_active: z.boolean(),
  firstname: ZodTrimmedString,
  lastname: ZodTrimmedString,
  gender: ZodB2bAdminGender.nullable().default(null),
  language_preference: ZodB2bAdminsLanguagePreference.optional(),
  phone_number: ZodMobilePhoneNumber.nullable().optional(),
  is_migrated: z.boolean().optional(),
  role_in_company: ZodTrimmedString.nullable(),
  role: ZodB2bAdminRoles,
  ...ZodB2bAdminCorrespondenceCreateRequest.shape,
});
export type PostCreateB2bAdminRequest = z.TypeOf<typeof ZodPostCreateB2bAdminRequest>;

export const ZodPostCreateB2bAdminResponse = z.object({
  id: ZodId,
  company_id: ZodId,
});
export type PostCreateB2bAdminResponse = z.TypeOf<typeof ZodPostCreateB2bAdminResponse>;

export const ZodPutEditB2bAdminRequest = z.object({
  ...ZodGetB2bAdminsOneResponse.pick({
    email: true,
    language_preference: true,
    is_third_party: true,
  }).shape,
  third_party_legal_structure: ZodTrimmedString.nullable().default(null),
  third_party_mandate_document: ZodEditThirdPartyMandateDocument.nullable().default(null),
  is_active: z.boolean(),
  firstname: ZodTrimmedString,
  lastname: ZodTrimmedString,
  // Optional so clients that don't send it keep the stored value; null clears it
  gender: ZodB2bAdminGender.nullable().optional(),
  phone_number: ZodMobilePhoneNumber.nullable().optional(),
  role_in_company: ZodTrimmedString.nullable(),
  role: ZodB2bAdminRoles,
  secondary_email: ZodSecureEmail.nullish(),
  ...ZodB2bAdminCorrespondenceEditRequest.shape,
});
export type PutEditB2bAdminRequest = z.TypeOf<typeof ZodPutEditB2bAdminRequest>;

export const ZodPutEditB2bAdminResponse = z.object({
  id: ZodId,
  company_id: ZodId,
});
export type PutEditB2bAdminResponse = z.TypeOf<typeof ZodPutEditB2bAdminResponse>;

export const ZodGetB2bAdminsLastOtpResponse = z.object({
  id: ZodId,
  code: ZodTrimmedString.nullable(),
});
export type GetB2bAdminsLastOtpResponse = z.TypeOf<typeof ZodGetB2bAdminsLastOtpResponse>;

export const getB2bAdminsEndpoint = endpoint({
  method: 'GET',
  queryParams: ZodGetB2bAdminsQuery,
  responseBody: ZodGetB2bAdminsResponse,
});

export type GetB2bAdminsEndpoint = GetEndpointType<typeof getB2bAdminsEndpoint>;

export const getB2bAdminsIdEndpoint = endpoint({
  method: 'GET',
  pathParams: ZodIDPathParam,
  responseBody: ZodGetB2bAdminsShowResponse,
});

export type GetB2bAdminsIdEndpoint = GetEndpointType<typeof getB2bAdminsIdEndpoint>;

export const postB2bAdminsEndpoint = endpoint({
  method: 'POST',
  body: ZodPostCreateB2bAdminRequest,
  responseBody: ZodPostCreateB2bAdminResponse,
});

export type PostB2bAdminsEndpoint = GetEndpointType<typeof postB2bAdminsEndpoint>;

export const putB2bAdminsIdEndpoint = endpoint({
  method: 'PUT',
  pathParams: ZodIDPathParam,
  body: ZodPutEditB2bAdminRequest,
  responseBody: ZodPutEditB2bAdminResponse,
});

export type PutB2bAdminsIdEndpoint = GetEndpointType<typeof putB2bAdminsIdEndpoint>;

export const getB2bAdminsLastOtpIdEndpoint = endpoint({
  method: 'GET',
  pathParams: ZodIDPathParam,
  responseBody: ZodGetB2bAdminsLastOtpResponse,
});

export type GetB2bAdminsLastOtpIdEndpoint = GetEndpointType<typeof getB2bAdminsLastOtpIdEndpoint>;

export const postB2bAdminsRequestOtpEndpoint = endpoint({
  method: 'POST',
  body: z.object({
    admin_id: ZodId,
  }),
  responseBody: z.object({
    id: ZodId,
    code: ZodTrimmedString,
  }),
});

export type PostB2bAdminsRequestOtpEndpoint = GetEndpointType<
  typeof postB2bAdminsRequestOtpEndpoint
>;

export const postB2bAdminsInvitesEndpoint = endpoint({
  method: 'POST',
  body: z.object({
    b2b_company_id: ZodId,
    admin_ids: ZodId.array().min(1),
  }),
  responseBody: z.object({
    id: ZodId,
  }),
});

export type PostB2bAdminsInvitesEndpoint = GetEndpointType<typeof postB2bAdminsInvitesEndpoint>;

export const deleteB2bAdminEndpoint = endpoint({
  method: 'DELETE',
  pathParams: ZodIDPathParam,
  responseBody: ZodSuccessResponse,
});

export type DeleteB2bAdminEndpoint = GetEndpointType<typeof deleteB2bAdminEndpoint>;
